import { useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Capability, resolveCapability } from './capability';
import { canModerateAuthoredContent } from './contentModeration';
import { actorForShow, ShowActor } from './showActor';

export interface ShowPermissions {
  /** The caller's standing on this show. */
  actor: ShowActor;
  /** Whether the caller holds `capability` on this show (or globally). */
  can: (capability: Capability) => boolean;
  /**
   * May the caller delete someone else's post, comment or photo written by an
   * author with this recorded role? Capability plus the rank check - the
   * author-owns-it branch stays at the call site.
   */
  canModerate: (capability: 'moderateShowPost' | 'moderateGalleryPhoto', authorRole: string | null | undefined) => boolean;
  /** Plain student or leader of this show - the student side of the product. */
  isStudentSide: boolean;
}

/**
 * Per-show permission lookups for the signed-in user.
 *
 * Reads the live profile from AuthContext, so it re-resolves when a teacher
 * promotes or demotes a leader mid-session. A UX gate, never a security
 * boundary - the backend re-checks every capability.
 */
export function useShowPermissions(showId?: string | null, showOrganizationId?: string | null): ShowPermissions {
  const { profile } = useAuth();

  const actor = useMemo(
    () => actorForShow(profile, showId, showOrganizationId),
    [profile, showId, showOrganizationId],
  );

  const can = useCallback(
    (capability: Capability) => resolveCapability(capability, { profile, showId, showOrganizationId }),
    [profile, showId, showOrganizationId],
  );

  const canModerate = useCallback(
    (capability: 'moderateShowPost' | 'moderateGalleryPhoto', authorRole: string | null | undefined) =>
      can(capability) && canModerateAuthoredContent(actor, authorRole),
    [can, actor],
  );

  return { actor, can, canModerate, isStudentSide: actor === 'student' || actor === 'showLeader' };
}

/** Global capabilities only (no show). */
export function useCan(): (capability: Capability) => boolean {
  const { profile } = useAuth();
  return useCallback((capability: Capability) => resolveCapability(capability, { profile }), [profile]);
}
